import React from "react";
import ReactMarkdown from "react-markdown";
import apiClient, { formatApiErrorDetail } from "@/lib/api";
import { FileText, Plus, Loader2, AlertCircle, CheckCircle2, Trash2, Eye, Edit3, X } from "lucide-react";

/**
 * v23.19 — Admin blog editor. Lightweight scaffold to seed SEO content before
 * AutoSEO ramps up. Markdown → auto-published (or draft) → indexed by Google.
 */
export default function AdminBlogPanel() {
  const [posts, setPosts] = React.useState([]);
  const [categories, setCategories] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [editing, setEditing] = React.useState(null); // post id | 'new' | null

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get("/admin/blog");
      setPosts(data.items || []);
      setCategories(data.categories || []);
      setError("");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load blog posts");
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const remove = async (post) => {
    if (!window.confirm(`Delete "${post.title}"? This cannot be undone.`)) return;
    try {
      await apiClient.delete(`/admin/blog/${post.id}`);
      await load();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Failed to delete");
    }
  };

  return (
    <section className="mb-12" data-testid="admin-blog-panel">
      <div className="flex items-end justify-between gap-3 mb-4 flex-wrap">
        <div>
          <h2 className="font-display text-2xl font-bold tracking-tight text-[#0A0A0A] inline-flex items-center gap-2">
            <FileText size={20} /> Blog editor
          </h2>
          <p className="mt-1 text-sm text-[#475569] max-w-2xl">Seed SEO content for city-targeted searches. Written posts publish immediately to /blog and are picked up by the sitemap.</p>
        </div>
        <button
          onClick={() => setEditing("new")}
          data-testid="blog-new-post-button"
          className="inline-flex items-center gap-2 bg-[#1E4D55] hover:bg-[#163C42] text-white px-4 py-2.5 text-sm font-semibold"
        >
          <Plus size={14} /> New post
        </button>
      </div>

      {error && (
        <div className="mb-3 flex items-start gap-2 text-sm text-[#B91C1C] bg-[#FEE2E2] border border-[#FCA5A5] p-3" data-testid="admin-blog-error">
          <AlertCircle size={16} className="mt-0.5 shrink-0" /><span>{error}</span>
        </div>
      )}

      {loading ? (
        <p className="text-sm text-[#475569] inline-flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading…</p>
      ) : posts.length === 0 ? (
        <div className="bg-white border border-dashed border-[#0A0A0A]/20 p-8 text-center" data-testid="admin-blog-empty">
          <FileText size={28} className="mx-auto text-[#475569]" />
          <p className="mt-3 text-sm font-semibold text-[#0A0A0A]">No posts yet</p>
          <p className="mt-1 text-xs text-[#475569]">Click &ldquo;New post&rdquo; to write your first article.</p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="admin-blog-list">
          {posts.map((p) => (
            <div key={p.id} className="bg-white border border-[#0A0A0A]/10 p-4 flex items-center gap-4 flex-wrap" data-testid={`admin-blog-row-${p.id}`}>
              <div className="flex-1 min-w-[220px]">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-semibold text-[#0A0A0A]">{p.title}</p>
                  {!p.is_published && (
                    <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#A66800] border border-[#F5A623]/40 bg-[#F5A623]/10 px-1.5 py-0.5">Draft</span>
                  )}
                  {p.source === "autoseo" && (
                    <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#1E4D55] border border-[#1E4D55]/30 bg-[#1E4D55]/5 px-1.5 py-0.5">AutoSEO</span>
                  )}
                </div>
                <p className="mt-1 text-xs text-[#475569]">
                  {(p.category || "").replace(/_/g, " ")}
                  {p.city && ` · ${p.city}`}
                  {p.published_at && ` · ${new Date(p.published_at).toLocaleDateString("en-GB")}`}
                  {` · /blog/${p.slug}`}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {p.is_published && (
                  <a href={`/blog/${p.slug}`} target="_blank" rel="noreferrer" data-testid={`admin-blog-view-${p.id}`} className="text-xs font-semibold text-[#1E4D55] hover:underline inline-flex items-center gap-1"><Eye size={12} /> View</a>
                )}
                <button onClick={() => setEditing(p.id)} data-testid={`admin-blog-edit-${p.id}`} className="text-xs font-semibold text-[#0A0A0A] hover:underline inline-flex items-center gap-1"><Edit3 size={12} /> Edit</button>
                <button onClick={() => remove(p)} data-testid={`admin-blog-delete-${p.id}`} className="text-xs font-semibold text-[#B91C1C] hover:underline inline-flex items-center gap-1"><Trash2 size={12} /> Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <BlogEditorModal
          postId={editing === "new" ? null : editing}
          categories={categories}
          onClose={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await load(); }}
        />
      )}
    </section>
  );
}

const EMPTY = {
  title: "",
  meta_description: "",
  city: "",
  category: "removals",
  body_markdown: "",
  hero_image_url: "",
  keywords: "",
  slug: "",
  is_published: true,
};

function BlogEditorModal({ postId, categories, onClose, onSaved }) {
  const [form, setForm] = React.useState(EMPTY);
  const [loading, setLoading] = React.useState(!!postId);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState("");
  const [preview, setPreview] = React.useState(false);

  React.useEffect(() => {
    if (!postId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await apiClient.get(`/admin/blog/${postId}`);
        if (cancelled) return;
        setForm({
          title: data.title || "",
          meta_description: data.meta_description || "",
          city: data.city || "",
          category: data.category || "removals",
          body_markdown: data.body_markdown || "",
          hero_image_url: data.hero_image_url || "",
          keywords: (data.keywords || []).join(", "),
          slug: data.slug || "",
          is_published: data.is_published !== false,
        });
      } catch (e) {
        if (!cancelled) setError(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load post");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [postId]);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      const payload = {
        title: form.title.trim(),
        meta_description: form.meta_description.trim(),
        city: form.city.trim() || null,
        category: form.category,
        body_markdown: form.body_markdown,
        hero_image_url: form.hero_image_url.trim() || null,
        keywords: form.keywords.split(",").map((k) => k.trim()).filter(Boolean),
        slug: form.slug.trim() || null,
        is_published: form.is_published,
        source: "manual",
      };
      if (postId) await apiClient.put(`/admin/blog/${postId}`, payload);
      else await apiClient.post("/admin/blog", payload);
      onSaved();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Failed to save");
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="blog-editor-modal">
      <div className="bg-white border border-[#0A0A0A]/10 max-w-4xl w-full my-8 max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-6 border-b border-[#0A0A0A]/10">
          <h3 className="font-display text-2xl font-bold tracking-tight text-[#0A0A0A]">{postId ? "Edit post" : "New post"}</h3>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPreview((v) => !v)}
              data-testid="blog-editor-preview-toggle"
              className={`text-xs font-semibold px-3 py-1.5 border transition-colors ${preview ? "bg-[#1E4D55] text-white border-[#1E4D55]" : "bg-white text-[#0A0A0A] border-[#0A0A0A]/20 hover:border-[#0A0A0A]"}`}
            >
              {preview ? "Edit" : "Preview"}
            </button>
            <button onClick={onClose} className="p-2 hover:bg-[#F5F5F5]"><X size={18} /></button>
          </div>
        </div>

        {loading ? (
          <div className="p-8 text-sm text-[#475569] inline-flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading…</div>
        ) : (
          <form onSubmit={submit} className="flex-1 overflow-y-auto p-6 space-y-4">
            {error && (
              <div className="flex items-start gap-2 text-sm text-[#B91C1C] bg-[#FEE2E2] border border-[#FCA5A5] p-3" data-testid="blog-editor-error">
                <AlertCircle size={16} className="mt-0.5 shrink-0" /><span>{error}</span>
              </div>
            )}

            {preview ? (
              <div className="prose max-w-none" data-testid="blog-editor-preview">
                <h1>{form.title || "(no title)"}</h1>
                <p><em>{form.meta_description}</em></p>
                {form.hero_image_url && <img src={form.hero_image_url} alt={form.title} />}
                <ReactMarkdown>{form.body_markdown}</ReactMarkdown>
              </div>
            ) : (
              <>
                <div className="grid sm:grid-cols-2 gap-3">
                  <Field label="Title" required>
                    <input required minLength={3} maxLength={200} value={form.title} onChange={update("title")} data-testid="blog-editor-title" className="w-full px-3 py-2.5 border border-[#0A0A0A]/15 text-sm" />
                  </Field>
                  <Field label="Slug (optional, auto-generated from title)">
                    <input maxLength={120} value={form.slug} onChange={update("slug")} placeholder="best-luton-van-tips-leeds" data-testid="blog-editor-slug" className="w-full px-3 py-2.5 border border-[#0A0A0A]/15 text-sm font-mono" />
                  </Field>
                </div>

                <Field label="Meta description (10-200 chars — shown in Google search results)" required>
                  <textarea required rows={2} minLength={10} maxLength={200} value={form.meta_description} onChange={update("meta_description")} data-testid="blog-editor-meta" className="w-full px-3 py-2.5 border border-[#0A0A0A]/15 text-sm" />
                  <p className="mt-1 text-[11px] text-[#475569]">{form.meta_description.length}/200</p>
                </Field>

                <div className="grid sm:grid-cols-3 gap-3">
                  <Field label="Category" required>
                    <select value={form.category} onChange={update("category")} data-testid="blog-editor-category" className="w-full px-3 py-2.5 border border-[#0A0A0A]/15 text-sm bg-white">
                      {categories.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                    </select>
                  </Field>
                  <Field label="City (optional)">
                    <input maxLength={60} value={form.city} onChange={update("city")} placeholder="leeds" data-testid="blog-editor-city" className="w-full px-3 py-2.5 border border-[#0A0A0A]/15 text-sm" />
                  </Field>
                  <Field label="Hero image URL (optional)">
                    <input maxLength={500} value={form.hero_image_url} onChange={update("hero_image_url")} placeholder="https://..." data-testid="blog-editor-hero" className="w-full px-3 py-2.5 border border-[#0A0A0A]/15 text-sm" />
                  </Field>
                </div>

                <Field label="Keywords (comma-separated, ≤20)">
                  <input maxLength={500} value={form.keywords} onChange={update("keywords")} placeholder="luton van leeds, removals near me, courier UK" data-testid="blog-editor-keywords" className="w-full px-3 py-2.5 border border-[#0A0A0A]/15 text-sm" />
                </Field>

                <Field label="Body (Markdown) — ≥50 chars" required>
                  <textarea required rows={16} minLength={50} maxLength={100000} value={form.body_markdown} onChange={update("body_markdown")} data-testid="blog-editor-body" className="w-full px-3 py-2.5 border border-[#0A0A0A]/15 text-sm font-mono" placeholder={"# Introduction\n\nWrite the article in Markdown here. Use **bold**, _italic_, [links](https://) and ## sub-headings.\n\n## Section 2\n\nMore content..."} />
                </Field>

                <label className="inline-flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={form.is_published} onChange={update("is_published")} data-testid="blog-editor-publish" />
                  Publish immediately (uncheck to save as draft)
                </label>
              </>
            )}

            <div className="pt-4 flex items-center justify-end gap-2 border-t border-[#0A0A0A]/10 sticky bottom-0 bg-white -mx-6 px-6 -mb-6 pb-6">
              <button type="button" onClick={onClose} className="px-4 py-2.5 text-sm font-semibold border border-[#0A0A0A]/20 hover:bg-[#F5F5F5]">Cancel</button>
              <button type="submit" disabled={saving} data-testid="blog-editor-save" className="inline-flex items-center gap-2 bg-[#1E4D55] hover:bg-[#163C42] text-white px-5 py-2.5 text-sm font-semibold disabled:opacity-60">
                {saving ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
                {saving ? "Saving…" : (postId ? "Save changes" : "Create post")}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-[0.18em] font-bold text-[#0A0A0A] mb-2">
        {label}{required && <span className="text-[#B91C1C]"> *</span>}
      </label>
      {children}
    </div>
  );
}
